import React, { useState, useEffect, useRef } from "react";
import { aiAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { fmtDateTime, fmtTime } from "../utils/dateUtils";
import {
  Bot, Send, Loader, BarChart3, AlertTriangle, Package,
  RefreshCw, Sparkles, Clock, ChevronDown, ChevronRight,
  Wrench, CheckCircle, XCircle, ArrowRight, Zap,
} from "lucide-react";

const SUGGESTED_QUESTIONS = [
  "How much stock do we have for the rest of the week?",
  "Which items are running low and need to be ordered urgently?",
  "How many vessels are missing this month?",
  "What is the attendance situation this week?",
  "Are there any anomalies in our operations I should know about?",
  "How is the breakfast program performing compared to the mid-day meal program?",
  "What were our top consumed ingredients this week?",
  "Which routes had the most delivery activity today?",
];

function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""} animate-fade-in-up`}>
      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold
        ${isUser ? "bg-[#E8956D]/20 text-[#E8956D]" : "bg-[#5C5147] text-white"}`}>
        {isUser ? "You" : <Bot size={16} />}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed
        ${isUser
          ? "bg-[#E8956D] text-[#3A322C] rounded-tr-sm"
          : "bg-white border border-[#E5E1DB] text-[#3A322C] rounded-tl-sm shadow-sm"}`}>
        {msg.content.split("\n").map((line, i) => (
          <span key={`idx-${i}`}>{line}{i < msg.content.split("\n").length - 1 && <br />}</span>
        ))}
        {msg.timestamp && (
          <p className={`text-[10px] mt-1.5 ${isUser ? "text-[#3A322C]/60" : "text-[#8A7E73]"}`}>
            {fmtTime(msg.timestamp)}
          </p>
        )}
      </div>
    </div>
  );
}

function AnomalyCard({ anomaly }) {
  const colors = {
    high: "border-[#C2624E] bg-[#FBEAE7]",
    medium: "border-[#D98032] bg-[#FDF4EB]",
    low: "border-[#E5E1DB] bg-white"
  };
  const textColors = { high: "text-[#C2624E]", medium: "text-[#D98032]", low: "text-[#5C5147]" };
  const icons = { stock_spike: "📈", vessel_loss: "🚢", attendance_drop: "👥", asset_warranty: "🔧" };
  return (
    <div className={`border rounded-xl p-4 ${colors[anomaly.severity]}`}>
      <div className="flex items-start gap-3">
        <span className="text-xl">{icons[anomaly.type] || "⚠"}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold uppercase ${textColors[anomaly.severity]}`}>{anomaly.severity}</span>
            <span className="text-xs text-[#8A7E73] bg-[#F4F3F0] px-2 py-0.5 rounded-full">{anomaly.type.replace("_", " ")}</span>
          </div>
          <p className="text-sm font-medium text-[#3A322C]">{anomaly.message}</p>
          <p className="text-xs text-[#8A7E73] mt-1.5 flex items-center gap-1">
            <ChevronRight size={11} /> {anomaly.action}
          </p>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "chat", label: "AI Chat", icon: Bot },
  { id: "diagnostics", label: "Smart Fix", icon: Zap },
  { id: "forecast", label: "Demand Forecast", icon: Package },
  { id: "report", label: "Weekly Report", icon: BarChart3 },
  { id: "anomalies", label: "Anomaly Detection", icon: AlertTriangle },
];

export default function AIAnalytics() {
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen } = useKitchen();
  const kitchenCtx = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  const navigate = useNavigate();
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);

  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("chat");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hello ${user?.name?.split(" ")[0] || ""}! I'm Manna, your AI operations assistant.\n\nI can help you understand what's happening across your kitchens — stock levels, vessel tracking, attendance, costs, and more.\n\nAsk me anything about your operations, or pick a suggested question below.`,
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [anomalies, setAnomalies] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportPeriod, setReportPeriod] = useState(7);
  const [forecastDays, setForecastDays] = useState(7);
  const chatEndRef = useRef(null);

  // Diagnostics state
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const [fixResults, setFixResults] = useState({});
  // Log reader state
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logType, setLogType] = useState("backend");
  const [logAnalysis, setLogAnalysis] = useState(null);
  const [logAnalysisLoading, setLogAnalysisLoading] = useState(false);
  const [issueDescription, setIssueDescription] = useState("");
  // Fix history
  const [fixHistory, setFixHistory] = useState([]);
  const [fixHistoryLoading, setFixHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Clarification
  const [clarifyingId, setClarifyingId] = useState(null);
  const [clarifyResult, setClarifyResult] = useState({});
  const [diagSubTab, setDiagSubTab] = useState("scan"); // scan | logs | history

  useEffect(() => {
    // Load kitchens into context if not already loaded — but DON'T override user's selection
    usersAPI.getKitchens().then(r => {
      const ks = r.data || [];
      if (ks.length > 0 && kitchenCtx?.loadKitchens) kitchenCtx.loadKitchens(ks);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const hasAccess = user?.is_admin || user?.permissions?.ai_bot;

  const sendMessage = async (text) => {
    const q = text || input.trim();
    if (!q) return;
    setInput("");
    setError("");
    setMessages(m => [...m, { role: "user", content: q, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await aiAPI.chat({ message: q, kitchen_id: selectedKitchen || null });
      setMessages(m => [...m, { role: "assistant", content: res.data.response, timestamp: new Date().toISOString() }]);
    } catch (e) {
      const msg = formatError(e);
      setMessages(m => [...m, { role: "assistant", content: `Sorry, I encountered an error: ${msg}`, timestamp: new Date().toISOString() }]);
    }
    setLoading(false);
  };

  const generateReport = async () => {
    setReportLoading(true); setError("");
    try {
      const res = await aiAPI.weeklyReport({ kitchen_id: selectedKitchen || null, period_days: reportPeriod });
      setReport(res.data);
    } catch (e) { setError(formatError(e)); }
    setReportLoading(false);
  };

  const runAnomalyDetection = async () => {
    setAnomalyLoading(true); setError("");
    try {
      const res = await aiAPI.detectAnomalies({ kitchen_id: selectedKitchen || null });
      setAnomalies(res.data);
    } catch (e) { setError(formatError(e)); }
    setAnomalyLoading(false);
  };

  const generateForecast = async () => {
    setForecastLoading(true); setError("");
    try {
      const res = await aiAPI.stockForecast({ kitchen_id: selectedKitchen || null, days_ahead: forecastDays });
      setForecast(res.data);
    } catch (e) { setError(formatError(e)); }
    setForecastLoading(false);
  };

  const runDiagnostics = async () => {
    setDiagLoading(true); setError(""); setFixResults({}); setClarifyResult({});
    try {
      const res = await aiAPI.diagnose(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setDiagData(res.data);
    } catch (e) { setError(formatError(e)); }
    setDiagLoading(false);
  };

  const handleApplyFix = async (issue) => {
    setFixingId(issue.id);
    try {
      const res = await aiAPI.applyFix({
        fix_type: issue.fix_type,
        params: issue.fix_params,
        issue_title: issue.title,
        issue_id: issue.id,
      });
      const result = res.data;
      if (result.redirect) {
        navigate(result.redirect);
        return;
      }
      setFixResults(prev => ({ ...prev, [issue.id]: { message: result.message, status: "success", risk_level: result.risk_level } }));
      setTimeout(() => runDiagnostics(), 1500);
    } catch (e) {
      setFixResults(prev => ({ ...prev, [issue.id]: { message: formatError(e), status: "error" } }));
    }
    setFixingId(null);
  };

  const handleClarify = async (issue) => {
    setClarifyingId(issue.id);
    try {
      const res = await aiAPI.requestClarification({
        issue_id: issue.id,
        issue_title: issue.title,
        issue_description: issue.description,
        user_context: "",
      });
      setClarifyResult(prev => ({ ...prev, [issue.id]: res.data.questions }));
    } catch (e) {
      setClarifyResult(prev => ({ ...prev, [issue.id]: `Error: ${formatError(e)}` }));
    }
    setClarifyingId(null);
  };

  const fetchLogs = async () => {
    setLogsLoading(true); setLogAnalysis(null);
    try {
      const res = await aiAPI.getLogs({ log_type: logType, lines: 80 });
      setLogs(res.data);
    } catch (e) { setError(formatError(e)); }
    setLogsLoading(false);
  };

  const analyzeLogs = async () => {
    if (!logs) return;
    setLogAnalysisLoading(true);
    try {
      const logText = logs.logs.map(l => l.raw).join("\n");
      const res = await aiAPI.analyzeLogs({
        log_content: logText,
        issue_description: issueDescription || "General system health check",
      });
      setLogAnalysis(res.data);
    } catch (e) { setError(formatError(e)); }
    setLogAnalysisLoading(false);
  };

  const fetchFixHistory = async () => {
    setFixHistoryLoading(true);
    try {
      const res = await aiAPI.getFixHistory({ limit: 30 });
      setFixHistory(res.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setFixHistoryLoading(false);
  };

  if (!hasAccess) return (
    <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
      <div className="w-16 h-16 bg-[#F4F3F0] rounded-2xl flex items-center justify-center mb-4">
        <Bot size={32} className="text-[#C4BAAF]" />
      </div>
      <h2 className="text-xl font-bold text-[#3A322C] font-[Manrope]">AI Analytics — Access Restricted</h2>
      <p className="text-[#8A7E73] mt-2 text-sm">Your role doesn't have AI Bot access. Ask your admin to enable it in User Management → Roles → AI Analytics toggle.</p>
    </div>
  );

  return (
    <div className="space-y-5 max-w-5xl" data-testid="ai-analytics">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C] flex items-center gap-3">
            <span className="w-10 h-10 bg-gradient-to-br from-[#E8956D] to-[#5C5147] rounded-xl flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </span>
            AI Analytics
          </h1>
          <p className="text-sm text-[#8A7E73] mt-0.5 ml-14">Powered by Claude Sonnet 4.5 · Data from your live operations</p>
        </div>
      </div>

      {error && <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-4 py-3 text-sm text-[#C2624E]">{error}</div>}

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* ── CHAT TAB ── */}
      {activeTab === "chat" && (
        <div className="flex flex-col h-[68vh] bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden shadow-sm">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FAFAF8]">
            {messages.map((msg, i) => <ChatBubble key={`idx-${i}`} msg={msg} />)}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#5C5147] flex items-center justify-center flex-shrink-0"><Bot size={16} className="text-white" /></div>
                <div className="bg-white border border-[#E5E1DB] rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2 shadow-sm">
                  <Loader size={14} className="animate-spin text-[#E8956D]" />
                  <span className="text-sm text-[#8A7E73]">Manna is thinking…</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Suggested questions */}
          {messages.length <= 2 && (
            <div className="px-4 py-3 border-t border-[#F4F3F0] bg-white">
              <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">Suggested questions</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_QUESTIONS.slice(0, 4).map((q, i) => (
                  <button key={`idx-${i}`} onClick={() => sendMessage(q)}
                    className="text-xs border border-[#E5E1DB] rounded-full px-3 py-1.5 text-[#5C5147] hover:bg-[#E8956D]/10 hover:border-[#E8956D] hover:text-[#E8956D] transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="px-4 py-3 border-t border-[#E5E1DB] bg-white flex gap-3 items-end">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask anything about your kitchen operations… (Enter to send)"
              rows={2}
              className="flex-1 border border-[#E5E1DB] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
              data-testid="ai-chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              className="w-10 h-10 bg-[#E8956D] rounded-xl flex items-center justify-center text-[#3A322C] hover:bg-[#D48661] disabled:opacity-50 transition-colors flex-shrink-0"
              data-testid="ai-chat-send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── SMART DIAGNOSTICS TAB ── */}
      {activeTab === "diagnostics" && (
        <div className="space-y-4">
          {/* Sub-tab bar */}
          <div className="flex gap-1 bg-[#F4F3F0] rounded-xl p-1 w-fit">
            {[
              { id: "scan", label: "Diagnostics Scan" },
              { id: "logs", label: "System Logs" },
              { id: "history", label: "Fix History" },
            ].map(t => (
              <button key={t.id} onClick={() => { setDiagSubTab(t.id); if (t.id === "history") fetchFixHistory(); }}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${diagSubTab === t.id ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
                {t.label}
                {t.id === "history" && fixHistory.length > 0 && <span className="ml-1.5 text-xs bg-[#E8956D] text-white rounded-full px-1.5 py-0.5">{fixHistory.length}</span>}
              </button>
            ))}
          </div>

          {/* ── SUB-TAB: SCAN ── */}
          {diagSubTab === "scan" && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-bold text-[#3A322C]">Smart Operations Diagnostics</h2>
                  <p className="text-sm text-[#8A7E73] mt-0.5">AI scans live data for problems and offers one-click fixes. Every fix is logged.</p>
                </div>
                <button onClick={runDiagnostics} disabled={diagLoading}
                  className="bg-[#5C5147] text-white rounded-xl px-5 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#4A4038] disabled:opacity-60 shrink-0"
                  data-testid="run-diagnostics-btn">
                  {diagLoading ? <Loader size={15} className="animate-spin" /> : <Zap size={15} />}
                  {diagLoading ? "Scanning..." : "Run Scan"}
                </button>
              </div>

              {!diagData && !diagLoading && (
                <div className="text-center py-20 bg-white border border-[#E5E1DB] rounded-2xl">
                  <div className="w-16 h-16 bg-[#F4F3F0] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Zap size={28} className="text-[#C4BAAF]" />
                  </div>
                  <h3 className="font-bold text-[#3A322C] mb-2">Ready to Scan</h3>
                  <p className="text-sm text-[#8A7E73] max-w-sm mx-auto">Click "Run Scan" to check 8 operational areas — data quality, stock, POs, routes, tasks, and more.</p>
                  <button onClick={runDiagnostics} className="mt-5 bg-[#E8956D] text-[#3A322C] rounded-xl px-6 py-2.5 font-bold text-sm inline-flex items-center gap-2">
                    <Zap size={15} /> Start Scan
                  </button>
                </div>
              )}

              {diagLoading && (
                <div className="bg-white border border-[#E5E1DB] rounded-2xl p-12 flex flex-col items-center gap-4 text-[#8A7E73]">
                  <Loader size={32} className="animate-spin text-[#E8956D]" />
                  <p className="font-medium">Scanning operations data…</p>
                  <p className="text-xs text-[#C4BAAF]">Checking stock, POs, indents, routes, tasks, expiry dates</p>
                </div>
              )}

              {diagData && !diagLoading && (
                <div className="space-y-4">
                  {/* Summary + confidence */}
                  <div className={`rounded-2xl p-5 border-2 ${diagData.total === 0 ? "border-[#637C64]/30 bg-[#EDF2EE]" : diagData.high > 0 ? "border-[#C2624E]/30 bg-white" : "border-[#D98032]/30 bg-white"}`}>
                    <div className="flex items-start gap-4 flex-wrap">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                          <span className="font-bold text-[#3A322C]">
                            {diagData.total === 0 ? "All clear! No issues found." : `${diagData.total} issue(s) detected`}
                          </span>
                          {diagData.high > 0 && <span className="text-xs bg-[#C2624E] text-white font-bold px-2 py-0.5 rounded-full">{diagData.high} High</span>}
                          {diagData.medium > 0 && <span className="text-xs bg-[#D98032] text-white font-bold px-2 py-0.5 rounded-full">{diagData.medium} Medium</span>}
                          {/* Confidence score */}
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ml-auto ${diagData.confidence_score >= 80 ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FDF4EB] text-[#D98032]"}`}>
                            Confidence: {diagData.confidence_score}%
                          </span>
                        </div>
                        <p className="text-sm text-[#5C5147] leading-relaxed whitespace-pre-line">{diagData.ai_summary}</p>
                      </div>
                      <p className="text-xs text-[#8A7E73] shrink-0">Scanned: {fmtTime(diagData.scanned_at)}</p>
                    </div>
                  </div>

                  {/* Issue cards */}
                  {diagData.issues.map((issue) => {
                    const fixResult = fixResults[issue.id];
                    const isFixing = fixingId === issue.id;
                    const isClarifying = clarifyingId === issue.id;
                    const clarifyQ = clarifyResult[issue.id];
                    const riskColors = { low: "bg-[#EDF2EE] text-[#637C64]", medium: "bg-[#FDF4EB] text-[#D98032]", high: "bg-[#FBEAE7] text-[#C2624E]", none: "bg-[#F4F3F0] text-[#8A7E73]" };

                    return (
                      <div key={issue.id} className="border border-[#E5E1DB] rounded-2xl bg-white overflow-hidden" data-testid={`issue-card-${issue.id}`}>
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              {/* Badges row */}
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${issue.severity === "high" ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#FDF4EB] text-[#D98032]"}`}>{issue.severity}</span>
                                <span className="text-xs bg-[#F4F3F0] text-[#8A7E73] px-2 py-0.5 rounded-full capitalize">{issue.category.replace("_", " ")}</span>
                                {issue.risk_level && issue.risk_level !== "none" && (
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${riskColors[issue.risk_level]}`}>
                                    Risk: {issue.risk_level}
                                  </span>
                                )}
                                {issue.count > 1 && <span className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full font-bold">{issue.count} items</span>}
                              </div>
                              <h3 className="font-bold text-[#3A322C] text-sm mb-1">{issue.title}</h3>
                              <p className="text-sm text-[#8A7E73] leading-relaxed">{issue.description}</p>

                              {/* Root cause */}
                              {issue.root_cause && (
                                <div className="mt-2 flex items-start gap-1.5">
                                  <span className="text-[10px] font-bold uppercase text-[#8A7E73] mt-0.5 shrink-0">Root cause:</span>
                                  <span className="text-xs text-[#5C5147]">{issue.root_cause}</span>
                                </div>
                              )}

                              {/* Risk explanation */}
                              {issue.risk_explanation && issue.risk_level !== "none" && (
                                <div className="mt-1 flex items-start gap-1.5">
                                  <span className="text-[10px] font-bold uppercase text-[#8A7E73] mt-0.5 shrink-0">If fixed:</span>
                                  <span className="text-xs text-[#5C5147]">{issue.risk_explanation}</span>
                                </div>
                              )}

                              {/* Affected records */}
                              {issue.records?.length > 0 && (
                                <div className="mt-3 bg-[#FAFAF8] rounded-lg p-3 space-y-1 border border-[#F4F3F0]">
                                  <p className="text-[10px] font-bold uppercase text-[#8A7E73] mb-1">Affected records</p>
                                  {issue.records.slice(0, 3).map((r, i) => (
                                    <p key={`idx-${i}`} className="text-xs text-[#5C5147] font-mono">{Object.values(r).filter(v => v && v !== r.id).join(" · ")}</p>
                                  ))}
                                  {issue.records.length > 3 && <p className="text-xs text-[#8A7E73]">+{issue.records.length - 3} more</p>}
                                </div>
                              )}
                            </div>

                            {/* Action column */}
                            <div className="flex flex-col items-end gap-2 shrink-0 min-w-[140px]">
                              {fixResult ? (
                                <div className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg ${fixResult.status === "success" ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                                  {fixResult.status === "success" ? <CheckCircle size={13} /> : <XCircle size={13} />}
                                  <span>{fixResult.message}</span>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleApplyFix(issue)}
                                  disabled={isFixing}
                                  className={`flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl transition-colors disabled:opacity-60 w-full justify-center ${issue.auto_fixable ? "bg-[#5C5147] text-white hover:bg-[#4A4038]" : "border border-[#E5E1DB] text-[#5C5147] hover:bg-[#F4F3F0]"}`}
                                  data-testid={`fix-btn-${issue.id}`}
                                >
                                  {isFixing ? <Loader size={13} className="animate-spin" /> : issue.auto_fixable ? <Zap size={13} /> : <ArrowRight size={13} />}
                                  {issue.fix_label}
                                </button>
                              )}
                              {/* Clarify button */}
                              {!fixResult && (
                                <button
                                  onClick={() => handleClarify(issue)}
                                  disabled={isClarifying}
                                  className="text-xs text-[#8A7E73] hover:text-[#E8956D] flex items-center gap-1 w-full justify-end"
                                  data-testid={`clarify-btn-${issue.id}`}
                                >
                                  {isClarifying ? <Loader size={11} className="animate-spin" /> : <Bot size={11} />}
                                  Ask Claude to clarify
                                </button>
                              )}
                              {issue.auto_fixable && !fixResult && (
                                <p className="text-[10px] text-[#637C64] font-bold uppercase">Auto-fixable</p>
                              )}
                            </div>
                          </div>

                          {/* Clarification result */}
                          {clarifyQ && (
                            <div className="mt-4 bg-gradient-to-r from-[#5C5147]/5 to-[#E8956D]/5 border border-[#E8956D]/20 rounded-xl p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Bot size={14} className="text-[#E8956D]" />
                                <span className="text-xs font-bold text-[#3A322C]">Claude's clarifying questions</span>
                              </div>
                              <p className="text-sm text-[#5C5147] whitespace-pre-line leading-relaxed">{clarifyQ}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex justify-end">
                    <button onClick={runDiagnostics} className="flex items-center gap-1.5 text-xs text-[#8A7E73] hover:text-[#5C5147]">
                      <RefreshCw size={12} /> Re-scan
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SUB-TAB: SYSTEM LOGS ── */}
          {diagSubTab === "logs" && (
            <div className="space-y-4">
              <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-4">
                <h3 className="font-bold text-[#3A322C]">System Log Reader</h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Log Source</label>
                    <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                      value={logType} onChange={e => setLogType(e.target.value)}>
                      <option value="backend">Backend Errors</option>
                      <option value="backend_out">Backend Output</option>
                      <option value="frontend">Frontend Errors</option>
                      <option value="nginx">Nginx / Proxy</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-48">
                    <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Describe the issue (optional)</label>
                    <input
                      className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                      placeholder="e.g. Login is failing, stock not updating..."
                      value={issueDescription}
                      onChange={e => setIssueDescription(e.target.value)}
                      data-testid="log-issue-input"
                    />
                  </div>
                  <button onClick={fetchLogs} disabled={logsLoading}
                    className="border border-[#E5E1DB] text-[#5C5147] rounded-xl px-4 py-2 text-sm font-bold hover:bg-[#F4F3F0] flex items-center gap-2 disabled:opacity-60">
                    {logsLoading ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Load Logs
                  </button>
                  {logs && (
                    <button onClick={analyzeLogs} disabled={logAnalysisLoading}
                      className="bg-[#5C5147] text-white rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-60 hover:bg-[#4A4038]"
                      data-testid="analyze-logs-btn">
                      {logAnalysisLoading ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      Analyze with AI
                    </button>
                  )}
                </div>

                {/* Log stats */}
                {logs && (
                  <div className="flex gap-3 flex-wrap">
                    <span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-3 py-1 rounded-full">{logs.lines_read} lines</span>
                    {logs.error_count > 0 && <span className="text-xs bg-[#FBEAE7] text-[#C2624E] font-bold px-3 py-1 rounded-full">{logs.error_count} errors</span>}
                    {logs.warning_count > 0 && <span className="text-xs bg-[#FDF4EB] text-[#D98032] font-bold px-3 py-1 rounded-full">{logs.warning_count} warnings</span>}
                    {!logs.has_errors && <span className="text-xs bg-[#EDF2EE] text-[#637C64] font-bold px-3 py-1 rounded-full">No errors detected</span>}
                  </div>
                )}
              </div>

              {/* AI Analysis result */}
              {logAnalysis && (
                <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={15} className="text-[#E8956D]" />
                    <h3 className="font-bold text-[#3A322C]">AI Root Cause Analysis</h3>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ml-auto ${logAnalysis.detected_risk === "high" ? "bg-[#FBEAE7] text-[#C2624E]" : logAnalysis.detected_risk === "medium" ? "bg-[#FDF4EB] text-[#D98032]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                      Risk: {logAnalysis.detected_risk}
                    </span>
                  </div>
                  <div className="text-sm text-[#5C5147] whitespace-pre-line leading-relaxed">{logAnalysis.analysis}</div>
                </div>
              )}

              {/* Raw log viewer */}
              {logs && (
                <div className="bg-[#1E1B18] rounded-2xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <span className="text-xs font-bold text-white/60 uppercase">{logType} logs</span>
                    <span className="text-xs text-white/40">{fmtTime(logs.read_at)}</span>
                  </div>
                  <div className="p-4 max-h-80 overflow-y-auto font-mono text-xs space-y-0.5">
                    {logs.logs.slice(-60).map((line, i) => (
                      <p key={`idx-${i}`} className={`leading-5 ${line.level === "ERROR" || line.level === "CRITICAL" ? "text-[#FF6B6B]" : line.level === "WARNING" ? "text-[#FFD93D]" : "text-white/60"}`}>
                        {line.raw}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {!logs && !logsLoading && (
                <div className="text-center py-12 text-[#8A7E73]">
                  <p className="text-sm">Select a log source and click "Load Logs" to view system logs</p>
                </div>
              )}
            </div>
          )}

          {/* ── SUB-TAB: FIX HISTORY ── */}
          {diagSubTab === "history" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-[#3A322C]">AI Fix Audit Trail</h3>
                <button onClick={fetchFixHistory} disabled={fixHistoryLoading} className="flex items-center gap-1.5 text-sm text-[#8A7E73] hover:text-[#5C5147]">
                  <RefreshCw size={13} className={fixHistoryLoading ? "animate-spin" : ""} /> Refresh
                </button>
              </div>
              <p className="text-sm text-[#8A7E73]">Every fix applied through the Smart Diagnostics panel is logged here with full details.</p>

              {fixHistoryLoading && <div className="text-center py-8"><Loader size={20} className="animate-spin text-[#E8956D] mx-auto" /></div>}

              {!fixHistoryLoading && fixHistory.length === 0 && (
                <div className="text-center py-16 bg-white border border-[#E5E1DB] rounded-2xl text-[#8A7E73]">
                  <Wrench size={32} className="mx-auto mb-3 opacity-20" />
                  <p>No fixes applied yet. Run a scan and apply a fix to see the audit trail here.</p>
                </div>
              )}

              {!fixHistoryLoading && fixHistory.length > 0 && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["When", "Issue", "Action", "Result", "Risk", "By"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wide text-[#5C5147] whitespace-nowrap">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {fixHistory.map((log, i) => (
                        <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 text-xs text-[#8A7E73] whitespace-nowrap">
                            {fmtDateTime(log.applied_at)}
                          </td>
                          <td className="px-4 py-3 text-[#3A322C] max-w-[180px]">
                            <p className="text-xs font-medium truncate">{log.issue_title || log.fix_type}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${log.action === "applied" ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#F4F3F0] text-[#8A7E73]"}`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#5C5147] max-w-[160px]">
                            <p className="truncate">{log.result}</p>
                            {log.fixed_count > 0 && <p className="text-[#637C64] font-bold">{log.fixed_count} record(s) changed</p>}
                          </td>
                          <td className="px-4 py-3">
                            {log.risk_level && log.risk_level !== "none" ? (
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${log.risk_level === "high" ? "bg-[#FBEAE7] text-[#C2624E]" : log.risk_level === "medium" ? "bg-[#FDF4EB] text-[#D98032]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                                {log.risk_level}
                              </span>
                            ) : <span className="text-xs text-[#C4BAAF]">—</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#8A7E73]">{log.applied_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── WEEKLY REPORT TAB ── */}
      {activeTab === "report" && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Report Period</label>
              <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                value={reportPeriod} onChange={e => setReportPeriod(parseInt(e.target.value))}>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
            <button onClick={generateReport} disabled={reportLoading}
              className="bg-[#5C5147] text-white rounded-xl px-6 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#4A4038] disabled:opacity-60">
              {reportLoading ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />}
              Generate Report
            </button>
            {report && (
              <p className="text-xs text-[#8A7E73] ml-auto">Generated: {fmtDateTime(report.generated_at)}</p>
            )}
          </div>

          {reportLoading && (
            <div className="flex items-center justify-center py-16 gap-3 text-[#8A7E73]">
              <Loader size={24} className="animate-spin text-[#E8956D]" />
              <span>Analysing your operations data and writing report…</span>
            </div>
          )}

          {report && !reportLoading && (
            <div className="space-y-4">
              {/* Stats mini-bar */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                  { label: "Gate Entries", value: report.data_summary?.gate_entries, color: "text-blue-600" },
                  { label: "Food Trips", value: report.data_summary?.food_distribution_trips, color: "text-[#637C64]" },
                  { label: "Vessels Missing", value: report.data_summary?.vessels_missing, color: report.data_summary?.vessels_missing > 0 ? "text-[#C2624E]" : "text-[#637C64]" },
                  { label: "Avg Attendance", value: report.data_summary?.avg_daily_attendance, color: "text-[#5C5147]" },
                  { label: "OT Hours", value: report.data_summary?.total_ot_hours, color: "text-[#D98032]" },
                  { label: "Indents Raised", value: report.data_summary?.indents_raised, color: "text-purple-600" },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-3 text-center">
                    <p className={`text-xl font-extrabold font-[Manrope] ${s.color}`}>{s.value ?? "—"}</p>
                    <p className="text-xs text-[#8A7E73] mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
              {/* AI narrative */}
              <div className="bg-white border border-[#E5E1DB] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} className="text-[#E8956D]" />
                  <h3 className="font-bold text-[#3A322C]">AI-Generated Report — {report.period}</h3>
                </div>
                <div className="prose prose-sm max-w-none text-[#5C5147] whitespace-pre-line leading-relaxed">
                  {report.report}
                </div>
              </div>
            </div>
          )}

          {!report && !reportLoading && (
            <div className="text-center py-16 text-[#8A7E73]">
              <BarChart3 size={48} className="mx-auto mb-3 opacity-20" />
              <p>Click "Generate Report" to get an AI-written analysis of your operations</p>
            </div>
          )}
        </div>
      )}

      {/* ── ANOMALY DETECTION TAB ── */}
      {activeTab === "anomalies" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[#8A7E73]">AI scans stock usage, vessel returns, attendance, and assets for unusual patterns</p>
            <button onClick={runAnomalyDetection} disabled={anomalyLoading}
              className="bg-[#C2624E] text-white rounded-xl px-5 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#a85040] disabled:opacity-60">
              {anomalyLoading ? <Loader size={15} className="animate-spin" /> : <AlertTriangle size={15} />}
              Scan Now
            </button>
          </div>

          {anomalyLoading && (
            <div className="flex items-center justify-center py-16 gap-3 text-[#8A7E73]">
              <Loader size={24} className="animate-spin text-[#E8956D]" />
              <span>Scanning operations data for anomalies…</span>
            </div>
          )}

          {anomalies && !anomalyLoading && (
            <div className="space-y-4">
              {anomalies.ai_summary && (
                <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={15} className="text-[#E8956D]" />
                    <h3 className="font-bold text-[#3A322C]">AI Analysis</h3>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ml-auto ${anomalies.high_severity > 0 ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                      {anomalies.count} anomalies · {anomalies.high_severity} high severity
                    </span>
                  </div>
                  <p className="text-sm text-[#5C5147] leading-relaxed whitespace-pre-line">{anomalies.ai_summary}</p>
                </div>
              )}

              {anomalies.anomalies?.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="font-bold text-[#3A322C]">Detected Issues ({anomalies.anomalies.length})</h3>
                  {anomalies.anomalies.map((a, i) => <AnomalyCard key={`idx-${i}`} anomaly={a} />)}
                </div>
              ) : (
                <div className="text-center py-10 text-[#637C64]">
                  <AlertTriangle size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-bold">No anomalies detected</p>
                  <p className="text-sm text-[#8A7E73] mt-1">{anomalies.message}</p>
                  {anomalies.tip && <p className="text-xs text-[#8A7E73] mt-2 italic">{anomalies.tip}</p>}
                </div>
              )}
            </div>
          )}

          {!anomalies && !anomalyLoading && (
            <div className="text-center py-16 text-[#8A7E73]">
              <AlertTriangle size={48} className="mx-auto mb-3 opacity-20" />
              <p>Click "Scan Now" to detect operational anomalies</p>
            </div>
          )}
        </div>
      )}

      {/* ── STOCK FORECAST TAB ── */}
      {activeTab === "forecast" && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Forecast Period</label>
              <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                value={forecastDays} onChange={e => setForecastDays(parseInt(e.target.value))}>
                <option value={7}>Next 7 days</option>
                <option value={14}>Next 14 days</option>
              </select>
            </div>
            <button onClick={generateForecast} disabled={forecastLoading}
              className="bg-[#637C64] text-white rounded-xl px-6 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#4e6350] disabled:opacity-60">
              {forecastLoading ? <Loader size={15} className="animate-spin" /> : <Package size={15} />}
              Generate Forecast
            </button>
          </div>

          {forecastLoading && (
            <div className="flex items-center justify-center py-16 gap-3 text-[#8A7E73]">
              <Loader size={24} className="animate-spin text-[#E8956D]" />
              <span>Analysing stock levels and usage patterns…</span>
            </div>
          )}

          {forecast && !forecastLoading && (
            <div className="space-y-4">
              {/* Urgent items */}
              {forecast.urgent_items?.length > 0 && (
                <div>
                  <h3 className="font-bold text-[#C2624E] mb-3 flex items-center gap-2">
                    <AlertTriangle size={16} /> Needs Reorder in Next {forecast.period_days} Days
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {forecast.urgent_items.map((item, i) => (
                      <div key={`idx-${i}`} className={`border rounded-xl p-3 ${item.days_of_stock_remaining <= 1 ? "border-[#C2624E] bg-[#FBEAE7]/30" : "border-[#D98032] bg-[#FDF4EB]/30"}`}>
                        <p className="font-bold text-[#3A322C] text-sm">{item.name}</p>
                        <p className={`text-xl font-extrabold font-[Manrope] mt-1 ${item.days_of_stock_remaining <= 1 ? "text-[#C2624E]" : "text-[#D98032]"}`}>
                          {item.days_of_stock_remaining}d
                        </p>
                        <p className="text-xs text-[#8A7E73]">of stock left</p>
                        <p className="text-xs text-[#5C5147] mt-1">{item.current} {item.unit} remaining</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* AI forecast narrative */}
              <div className="bg-white border border-[#E5E1DB] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} className="text-[#637C64]" />
                  <h3 className="font-bold text-[#3A322C]">Procurement Forecast — Next {forecast.period_days} Days</h3>
                </div>
                <div className="text-sm text-[#5C5147] whitespace-pre-line leading-relaxed">{forecast.forecast}</div>
              </div>
            </div>
          )}

          {!forecast && !forecastLoading && (
            <div className="text-center py-16 text-[#8A7E73]">
              <Package size={48} className="mx-auto mb-3 opacity-20" />
              <p>Click "Generate Forecast" to predict what needs to be purchased</p>
              <p className="text-xs mt-1 text-[#C4BAAF]">Accuracy improves as more stock data is entered</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
